import {useContext} from 'react';

import {useFormik} from 'formik';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import {Divider, Grid, TextField, Typography} from '@mui/material';

import {
  ChangePasswordFormValues,
  getTextFieldStyles,
  initialFormValues,
  validateChangePasswordForm,
} from './changePasswordFormUtils';
import {EmpionButton} from '../../../../../../components/EmpionButton/EmpionButton';
import {SnackBarContext} from '../../../../../../contexts/SnackBarContext/SnackBarContext';
import {UserContext} from '../../../../../../contexts/UserContext/UserContext';
import {useApiWrite} from '../../../../../../hooks/useApiWrite';
import {greenColors} from '../../../../../../layouts/theme/designSystem';
import {Put} from '../../../../../../lib/api/PUT/Put';
import {PutRoutes} from '../../../../../../lib/api/PUT/PutRoutes';
import {AppConfig} from '../../../../../../lib/AppConfig';
import {logout as authServiceLogout} from '../../../../../../services/auth-service';

export const ChangePasswordForm = () => {
  const {t} = useTranslation();
  const {dispatchMessage} = useContext(SnackBarContext);
  const {userContextLogout} = useContext(UserContext);
  const navigate = useNavigate();

  const onSuccess = () => {
    dispatchMessage(t('passwordReset.successMessage'), 'success');
    authServiceLogout();
    userContextLogout();
    navigate(AppConfig.LOGIN_PATH);
  };

  const onError = () => {
    dispatchMessage(t('passwordReset.errorMessage'), 'error');
  };

  const {makeRequest} = useApiWrite<Put.UpdatePassword>({
    method: 'put',
    path: PutRoutes[Put.UpdatePassword],
    onSuccess,
    onError,
  });

  const formik = useFormik<ChangePasswordFormValues>({
    initialValues: initialFormValues,
    validate: (values) => validateChangePasswordForm(values, t),
    onSubmit: (values) => {
      makeRequest({
        currentPassword: values.oldPassword,
        newPassword: values.newPassword,
        newPasswordConfirmation: values.repeatNewPassword,
      });
    },
  });

  const textFieldStyles = getTextFieldStyles();

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container>
        <Grid item xs={12} mb={2}>
          <Typography variant='h3'>{t('passwordReset.changePasswordTitle')}</Typography>
          <Typography variant='body1'>{t('passwordReset.changePasswordSubtitle')}</Typography>
          <Typography variant='caption' color='text.secondary' mt={1} display='block'>
            {t('registration.signUp.formLabels.passwordRequirements')}
          </Typography>
        </Grid>
        <Divider sx={{width: '100%', marginBottom: '16px'}} />
        <Grid item container xs={12} spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant='body2' mb={1}>
              {t('passwordReset.oldPasswordLabel')}
            </Typography>
            <TextField
              fullWidth
              type='password'
              name='oldPassword'
              autoComplete='current-password'
              value={formik.values.oldPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.oldPassword && !!formik.errors.oldPassword}
              helperText={formik.touched.oldPassword && formik.errors.oldPassword}
              data-testid='old-password-field'
              size='small'
              sx={textFieldStyles}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant='body2' mb={1}>
              {t('passwordReset.passwordLabel')}
            </Typography>
            <TextField
              fullWidth
              type='password'
              name='newPassword'
              autoComplete='new-password'
              value={formik.values.newPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.newPassword && !!formik.errors.newPassword}
              helperText={formik.touched.newPassword && formik.errors.newPassword}
              data-testid='new-password-field'
              size='small'
              sx={textFieldStyles}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant='body2' mb={1}>
              {t('passwordReset.passwordRepeatLabel')}
            </Typography>
            <TextField
              fullWidth
              type='password'
              name='repeatNewPassword'
              autoComplete='new-password'
              value={formik.values.repeatNewPassword}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.repeatNewPassword && !!formik.errors.repeatNewPassword}
              helperText={formik.touched.repeatNewPassword && formik.errors.repeatNewPassword}
              data-testid='repeat-new-password-field'
              size='small'
              sx={textFieldStyles}
            />
          </Grid>
        </Grid>
        <Grid item container xs={12} justifyContent={{xs: 'center', sm: 'flex-end'}} mt={2}>
          <EmpionButton
            type='submit'
            disabled={!formik.isValid}
            hoverBackgroundColor={greenColors[100]}
            sx={{width: {xs: '100%', sm: 'auto'}, backgroundColor: greenColors[700]}}
            disabledColor={greenColors[500]}
          >
            {t('passwordReset.button')}
          </EmpionButton>
        </Grid>
      </Grid>
    </form>
  );
};
